import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type SurveyViewedAction } from '@/models/analytics/features/survey';
import {
  DICTATION_CSAT_SURVEY_ACTION,
  type DictationCsatAnalyticsPorts,
  type DictationCsatSurveyActionToken,
  type TrackDictationCsatDismissed,
  type TrackDictationCsatSubmitted,
  type TrackDictationCsatViewed,
} from '@heidi/dictate/contracts';

/**
 * App-side emit hosts for the post-dictation CSAT popover (`@heidi/dictate`).
 *
 * ROUTING: `{ amplitude: false }` on all three, as all three Rematch wrappers
 * passed — the OPPOSITE of the session host next door, which passes none. The two
 * are deliberately not normalised against each other.
 *
 * The enum members, not `dictationCsatSubmitted.name`: `event_name` discriminates
 * `TrackingEventV2` on string ENUM members. The suite asserts they agree.
 */

/**
 * The package's `view_action` token bound to the app vocabulary that declares it.
 * `SurveyViewedAction` is a string-literal union, not an enum, so `satisfies`
 * proves TOTALITY only — the suite asserts the emitted string on top, because a
 * token pointed at the wrong member still satisfies the constraint.
 */
const SURVEY_ACTION_BY_TOKEN = {
  [DICTATION_CSAT_SURVEY_ACTION.shown]: 'universal_dictation_csat_shown',
} as const satisfies Record<DictationCsatSurveyActionToken, SurveyViewedAction>;

export const trackDictationCsatViewed: TrackDictationCsatViewed = ({ view_action }) => {
  track({
    event_name: TrackingEventName.SurveyViewed,
    props: { view_action: SURVEY_ACTION_BY_TOKEN[view_action] },
    options: { amplitude: false },
  });
};

export const trackDictationCsatSubmitted: TrackDictationCsatSubmitted = (props) => {
  track({
    event_name: TrackingEventName.DictationCsatSubmitted,
    props,
    options: { amplitude: false },
  });
};

export const trackDictationCsatDismissed: TrackDictationCsatDismissed = (props) => {
  track({
    event_name: TrackingEventName.DictationCsatDismissed,
    props,
    options: { amplitude: false },
  });
};

/**
 * All three seams as one bundle for the eventual composition point. A module
 * constant, so it cannot re-fire the impression effect or re-arm the timer.
 */
export const dictationCsatAnalyticsPorts: DictationCsatAnalyticsPorts = {
  trackViewed: trackDictationCsatViewed,
  trackSubmitted: trackDictationCsatSubmitted,
  trackDismissed: trackDictationCsatDismissed,
};
